import { useEffect, type ReactNode } from "react";
import { Icon } from "../icons/Icon";
import "../../styles/components/drawer.css";

export interface DrawerProps {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  /** 宽度，默认 520；宽表详情可传 720+ */
  width?: number;
  /** 底部固定操作区（详情页的「通过/驳回/保存」放这里，滚动时不丢失） */
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}

/**
 * 右侧抽屉：列表页的「查看/编辑详情」统一载体（避免整屏跳转丢上下文）。
 * 与弹窗的分工：轻量表单用 Modal，多区块详情（主数据 + 流水 + 操作）用 Drawer。
 */
export function Drawer({ open, title, subtitle, width = 520, footer, onClose, children }: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="drawer-mask" onClick={onClose}>
      <aside
        className="drawer"
        style={{ width }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="drawer-head">
          <div className="drawer-head-main">
            <h3 className="drawer-title">{title}</h3>
            {subtitle ? <p className="drawer-subtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="drawer-close" aria-label="关闭" onClick={onClose}>
            <Icon name="close" size={15} />
          </button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-footer">{footer}</footer> : null}
      </aside>
    </div>
  );
}

export default Drawer;
